import React from "react";
import moment from "moment-timezone";
import twix from "twix";
import { Link, Navigation } from "react-router";
import { Avatar, ListItem, RaisedButton } from "material-ui";
import {
  getLuminance,
  lighten,
  darken
} from "material-ui/utils/colorManipulator";
import Tag from "../tag.jsx";
import TagList from "../tag-list.jsx";
import TicketButton from "../ticket-button.jsx";
import VirtualScreeningIcon from "../icons/virtual-screening-icon.jsx";
import PhysicalEventIcon from "../icons/physical-event-icon.jsx";
import {
  IS_MOBILE,
  getColorForTag,
  highlightTextAtIndices,
  isApp
} from "../../../util";

class EventListItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hovered: false
    };

    this._onClick = this._onClick.bind(this);
    this._makeRightIcon = this._makeRightIcon.bind(this);
    this.getGradient = this.getGradient.bind(this);
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      muiTheme: React.PropTypes.object
    };
  }

  _onClick(e) {
    if (this.props.useVirtualizedMode) {
      const newTab =
        e.ctrlKey ||
        e.shiftKey ||
        e.metaKey || // apple
        (e.button && e.button == 1) || // middle click, >IE9 + everyone else
        isApp(); // on mobile app, if we open in window it won't refocus it

      window.open(
        `/schedule/${this.props.item.id}`,
        newTab ? "_blank" : "scheduleDetail"
      );
    } else {
      this.context.router.push({
        pathname: `/schedule/${this.props.item.id}`,
        state: { fromSchedule: true }
      });
    }
  }

  _makeRightIcon(compact) {
    let styles = this.getStyles(),
      isLarge = this.props.isLarge,
      isCompact = compact,
      button = (
        <TicketButton
          hideButton={this.props.item && this.props.item.hide_tickets_button}
          event={this.props.item}
          inline={!isLarge}
          compact={isCompact}
          fromSchedule={true}
          noMultiContainer={isCompact}
          appSettings={this.props.appSettings}
          useVirtualizedMode={this.props.useVirtualizedMode}
        />
      );

    if (isLarge) {
      const isHybridStyle =
        this.props.item &&
        this.props.item.is_dated &&
        this.props.appSettings &&
        this.props.appSettings.schedule_include_virtual;
      return (
        <div
          style={
            isCompact
              ? this.context.muiTheme.prepareStyles(
                  isHybridStyle
                    ? styles.rightIconCompactWithHybrid
                    : styles.rightIconCompact
                )
              : null
          }
        >
          {button}
        </div>
      );
    } else {
      return null;
    }
  }

  getStyles() {
    return {
      listItem: {
        position: "relative",
        fontSize: 16,
        cursor: "pointer",
        color: "rgb(33, 33, 33)",
        minHeight: 60
      },
      rightIconCompactWithHybrid: {
        alignSelf: "center",
        marginLeft: 5
      },
      rightIconCompact: {
        marginLeft: 5
      },
      tagList: {},
      tagListCompact: {},
      tagListCompatLarge: {
        display: "inline",
        marginLeft: 5,
        verticalAlign: 1
      },
      timeLabel: {
        display: "inline-block",
        marginRight: 8,
        fontWeight: "700",
        fontSize: "0.8em"
      },
      timeLabelLarge: {
        display: "inline-block",
        fontSize: "0.8em",
        fontWeight: "700",
        paddingRight: 10,
        minWidth: 60
      },
      timeLabelLargeWithDate: {
        display: "inline-block",
        fontSize: "0.8em",
        fontWeight: "700",
        paddingRight: 10,
        minWidth: 105
      },
      primaryText: {
        lineHeight: "24px",
        padding: 12,
        paddingLeft: 16
      },
      primaryTextLarge: {
        display: "flex",
        alignItems: "baseline"
      },
      largeInnerContainer: {
        flex: 1
      },
      secondaryText: {
        fontSize: 14,
        lineHeight: "16px",
        marginTop: 4,
        color: "rgba(0, 0, 0, 0.54)"
      },
      description: {
        fontStyle: "italic",
        wordWrap: "break-word"
      },
      diagonalLines: {
        backgroundSize: "30px 30px"
      }
    };
  }

  getGradient(gColor, isTag) {
    const altColor = isTag ? darken(gColor, 0.07) : lighten(gColor, 0.09);
    return (
      "-webkit-linear-gradient(left top, " +
      gColor +
      " 0%, " +
      gColor +
      " 25%, " +
      altColor +
      " 25%, " +
      altColor +
      " 50%, " +
      gColor +
      " 50%, " +
      gColor +
      " 75%, " +
      altColor +
      " 75%)"
    );
  }

  render() {
    let { prepareStyles } = this.context.muiTheme,
      event = this.props.item,
      {
        includeDateInCompact,
        includeTimeInCompact,
        compact,
        isLarge,
        appSettings
      } = this.props,
      styles = this.getStyles(),
      startMoment = moment(event.start_time),
      includeYearForCompact = !startMoment.isSame(moment(), "year"),
      dateFmtCompactFull = includeYearForCompact ? "MMM D, YYYY, LT" : "MMM D, LT",
      twixOptions = {
        showDate: this.props.showExtended,
        showDayOfWeek: true,
        monthFormat: "MMMM",
        weekdayFormat: "dddd,"
      },
      // Twix range formatting: omit year if the whole range is in the current year; otherwise include it
      endMoment = moment(event.end_time),
      wholeRangeInCurrentYear =
        startMoment.isSame(moment(), "year") && endMoment.isSame(moment(), "year"),
      timeRange = event.is_dated
        ? startMoment
            .twix(endMoment)
            .format(
              wholeRangeInCurrentYear
                ? Object.assign({}, twixOptions, { yearFormat: "YYYY", implicitYear: true })
                : Object.assign({}, twixOptions, { yearFormat: "YYYY", implicitYear: false })
            )
        : "Undated event",
      style = Object.assign(
        {},
        styles.listItem,
        isLarge && styles.listItemLarge,
        styles.diagonalLines
      );

    // Highlight matchec content if necessary
    if (event._search_match_details) {
      event = Object.assign({}, event); // so we can update html inline
      event._search_match_details.forEach(({ key, indices }) => {
        if (key === "name") {
          event.html_name = highlightTextAtIndices(event.name, indices);
        } else if (key === "short_description") {
          event.html_short_description = highlightTextAtIndices(
            event.short_description,
            indices
          );
          compact = false; // force show the description
        }
      });
    }

    let venueTags =
        event.venue && event.venue.id && event.venue.id !== "Virtual"
          ? [
              {
                id: event.venue.id,
                name: event.venue.short_name || event.venue.name,
                color: event.venue.color
              }
            ]
          : [],
      tags =
        this.context.tenant.schedule_dominant_tag === "venue"
          ? venueTags.concat(event.tags)
          : event.tags.concat(venueTags);

    let timeLabelColor = "#9C9C9C";

    if (compact) {
      style.backgroundColor = tags.length
        ? getColorForTag(
            tags[0],
            true,
            this.context.tenant.schedule_invert_colors ? 0.5 : 0.4,
            this.context.tenant
          )
        : "#ccc";
      style.margin = "10px 0";
      if (event.is_virtual) {
        style.backgroundImage = this.getGradient(
          style.backgroundColor,
          tags.length
        );
      }
      if (getLuminance(style.backgroundColor) < 0.2) {
        timeLabelColor = "#fff";
      }
    } else {
      style.backgroundColor = tags.length
        ? getColorForTag(tags[0], true, 0.3, this.context.tenant)
        : "#ccc";
      if (event.is_virtual) {
        style.backgroundImage = this.getGradient(
          style.backgroundColor,
          tags.length
        );
      }
      style.margin = "10px 0";
    }

    if (getLuminance(style.backgroundColor) > 0.95) {
      style.border = "1px solid #ccc";
    }

    return (
      <div
        style={prepareStyles(style)}
        className="EventList--EventListItem"
        onClick={this._onClick}
      >
        <div
          style={prepareStyles(
            Object.assign(
              {},
              styles.primaryText,
              isLarge && compact && styles.primaryTextLarge
            )
          )}
        >
          {!compact && this._makeRightIcon(compact)}
          {event.is_dated && compact ? (
            <div
              style={Object.assign(
                {},
                isLarge
                  ? includeDateInCompact
                    ? styles.timeLabelLargeWithDate
                    : styles.timeLabelLarge
                  : styles.timeLabel,
                { color: timeLabelColor }
              )}
            >
              {includeDateInCompact
                ? startMoment.format(dateFmtCompactFull)
                : startMoment.format("LT")}
            </div>
          ) : null}
          <div
            style={isLarge ? prepareStyles(styles.largeInnerContainer) : null}
          >
            {event.html_name ? (
              <span dangerouslySetInnerHTML={{ __html: event.html_name }} />
            ) : (
              event.name
            )}
            <TagList
              style={
                compact
                  ? isLarge
                    ? styles.tagListCompatLarge
                    : styles.tagListCompact
                  : styles.tagList
              }
              tags={tags}
              tooltip={t => `Filter events by "${t}"`}
              onClick={
                !IS_MOBILE
                  ? tag => {
                      if (tag.id === event.venue.id) {
                        this.props.onFilterVenueChange(event.venue.id, true, {
                          ignoreScrollBehavior: false
                        });
                      } else {
                        this.props.onTagChange(tag.id, true, {
                          ignoreScrollBehavior: false
                        });
                      }
                    }
                  : undefined
              }
            />
            {compact ? (
              event.is_dated && appSettings.schedule_include_virtual ? (
                <div style={styles.secondaryText}>
                  {event.is_virtual ? (
                    <VirtualScreeningIcon
                      styles={{
                        float: "left",
                        paddingRight: 12,
                        marginTop: -2,
                        marginBottom: -20,
                        width: 14,
                        fillOpacity: 0.85
                      }}
                      scale="0.9"
                    />
                  ) : (
                    <PhysicalEventIcon
                      styles={{
                        float: "left",
                        paddingRight: 12,
                        marginTop: -4,
                        marginBottom: -20,
                        width: 14,
                        fillOpacity: 0.85
                      }}
                      scale="0.9"
                    />
                  )}
                  {event.is_dated ? (
                    <div className="pull-left" style={styles.secondaryText}>
                      {timeRange +
                        (event.location ? " @ " + event.location : "")}
                    </div>
                  ) : null}
                </div>
              ) : null
            ) : (
              <div style={styles.secondaryText}>
                <div>
                  {timeRange + (event.location ? " @ " + event.location : "")}
                </div>
                {event.html_short_description ? (
                  <div
                    style={styles.description}
                    dangerouslySetInnerHTML={{
                      __html: event.html_short_description
                    }}
                  />
                ) : (
                  <div style={styles.description}>
                    {event.short_description}
                  </div>
                )}
              </div>
            )}
          </div>
          {compact && this._makeRightIcon(compact)}
        </div>
      </div>
    );
  }
}

export default EventListItem;



// WEBPACK FOOTER //
// ./src/app/components/partials/event-list/event-list-item.jsx